import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchIcon, XIcon } from 'lucide-react';
import { PageHero } from '../components/ui/PageHero';
import { QuoteBanner } from '../components/ui/QuoteBanner';
import { ReviewNote } from '../components/ui/ReviewNote';
import { ProjectCard } from '../components/projects/ProjectCard';
import { ProjectFilters } from '../components/projects/ProjectFilters';
import { useSeo } from '../hooks/useSeo';
import { pageMeta } from '../data/seo';
import { projects } from '../data/projects';
import { heroImages } from '../data/images';
import { categoryLabel, countByCategory, filterProjects, isCategoryFilter, type CategoryFilter } from '../utils/projects';
import { buttonClass } from '../utils/buttonStyles';
export function Projects() {
  useSeo(pageMeta.projects);
  const [params, setParams] = useSearchParams();
  const rawCategory = params.get('category');
  const category: CategoryFilter = isCategoryFilter(rawCategory) ? rawCategory : 'all';
  const query = params.get('q') ?? '';
  const counts = useMemo(() => countByCategory(projects), []);
  const results = useMemo(() => filterProjects(projects, category, query), [category, query]);
  const update = (next: {
    category?: CategoryFilter;
    q?: string;
  }) => {
    const p = new URLSearchParams(params);
    if (next.category !== undefined) {
      if (next.category === 'all') p.delete('category');else p.set('category', next.category);
    }
    if (next.q !== undefined) {
      if (next.q) p.set('q', next.q);else p.delete('q');
    }
    setParams(p, {
      replace: true
    });
  };
  return <>
      <PageHero title="Projects" intro="Roadworks, water and sanitation, mining earthworks, buildings and maintenance projects documented in the company portfolio." image={heroImages.roads} breadcrumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Projects'
    }]} data-id="element-749" data-mp="2hhm9vq2vdr6l" />

      <section aria-labelledby="portfolio-title" className="bg-charcoal-950 py-16 md:py-20" data-id="element-750" data-mp="2cfwvq1kcoxea">
        <div className="mx-auto max-w-site px-5 md:px-8" data-id="element-751" data-mp="1tdmkq0kuesow">
          <h2 id="portfolio-title" className="sr-only" data-id="element-752" data-mp="1oktgrex77odv">
            Project portfolio
          </h2>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between" data-id="element-753" data-mp="1jlqo6tyenams">
            <ProjectFilters active={category} counts={counts} onChange={c => update({
            category: c
          })} data-id="element-754" data-mp="1enp5490zbbbb" />
            <div className="relative w-full shrink-0 lg:w-80" data-id="element-755" data-mp="2cb7cpgiwm7dq">
              <label htmlFor="project-search" className="sr-only" data-id="element-756" data-mp="2c8l4rfs3ppuz">
                Search projects
              </label>
              <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-steel-400" aria-hidden="true" data-id="element-757" data-mp="22chu1lj6oiwf" />
              <input id="project-search" type="search" value={query} onChange={e => update({
              q: e.target.value
            })} placeholder="Search by name, place or client" className="h-11 w-full border border-white/20 bg-charcoal-900 pl-11 pr-10 text-[15px] text-white placeholder:text-steel-500 focus:border-brand-light focus:outline-none" data-id="element-758" data-mp="1xd57l1w85dud" />
              {query && <button type="button" onClick={() => update({
              q: ''
            })} className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center text-steel-300 hover:text-white" aria-label="Clear search" data-id="element-759" data-mp="37jrdi0mveo49">
                  <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-760" data-mp="3la4n07feuavp" />
                </button>}
            </div>
          </div>

          <p className="mt-8 text-sm text-steel-400" aria-live="polite" data-id="element-761" data-mp="3q8po092hkyl7">
            Showing {results.length} of {projects.length} projects
            {category !== 'all' && ` in ${categoryLabel(category)}`}
            {query && ` matching “${query}”`}
          </p>

          {results.length > 0 ? <ul className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-762" data-mp="20xfdleyndnu3">
              {results.map(project => <li key={project.id} data-id="element-763" data-mp="3mdv8asinirh1">
                  <ProjectCard project={project} data-id="element-764" data-mp="3mctq8437x4pt" />
                </li>)}
            </ul> : <div className="mt-6 border border-dashed border-white/20 px-6 py-16 text-center" data-id="element-765" data-mp="4774d5zc8acy">
              <p className="font-display text-[26px] font-bold uppercase text-white" data-id="element-766" data-mp="76fssiiyis6">No projects match</p>
              <p className="mt-2 text-steel-300" data-id="element-767" data-mp="38k413qse7joh">Try another category or a different search term.</p>
              <button type="button" onClick={() => setParams({}, {
            replace: true
          })} className={buttonClass('outline', 'md', 'mt-6')} data-id="element-768" data-mp="33l0va44l99fg">
                Clear filters
              </button>
            </div>}

          <ReviewNote className="mt-12" data-id="element-769" data-mp="2oy7pbpgylfzx">
            Records are taken from the October 2026 company profile and the two project banners. Images marked
            "Representative image" are illustrations; replace them with the original project photographs before launch.
            To hide a record, set publication to "draft" in the project data.
          </ReviewNote>
        </div>
      </section>
      <QuoteBanner title="Have a similar project in mind?" data-id="element-770" data-mp="20uj93zvkykqc" />
    </>;
}